import React, {useState} from "react"
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	CircularProgress,
	Chip,
	Divider,
	RadioGroup,
	FormControlLabel,
	Radio,
	TextField,
	Alert,
} from "@mui/material"

import {phraseWaitParty} from "./waitPartyPhrasing"

import {api} from "@indebted/api"
import type {ActiveCase, CaseWaitParty} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

const STANDARD_DEADLINE = "STANDARD"
const PICKED_DEADLINE = "PICKED_DATE"

const waitReasonByPartyID: Record<string, string | undefined> = {
	CUSTOMER: "CUSTOMER_RESPONSE",
	CLIENT: "CLIENT_ACTION",
}

function todayInAgentTimezone(): string {
	const today = new Date()
	const month = `${today.getMonth() + 1}`.padStart(2, "0")
	const day = `${today.getDate()}`.padStart(2, "0")
	return `${today.getFullYear()}-${month}-${day}`
}

function endOfDayInAgentTimezone(calendarDate: string): string {
	const [year, month, day] = calendarDate.split("-").map(Number)
	return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString()
}

type ParkCaseModalProps = {
	customerID: string
	activeCase: ActiveCase
	onClose: () => void
	onParked: () => void
}

function ParkCaseModal({customerID, activeCase, onClose, onParked}: ParkCaseModalProps) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()

	const [selectedPartyID, setSelectedPartyID] = useState("")
	const [deadlineChoice, setDeadlineChoice] = useState(STANDARD_DEADLINE)
	const [pickedDate, setPickedDate] = useState("")
	const [submitting, setSubmitting] = useState(false)

	const selectedParty = activeCase.WaitParties.find((party) => party.ID === selectedPartyID)
	const waitRunsToDeadline = selectedParty?.HasDeadline ?? false
	const deadlineIsPicked = waitRunsToDeadline && deadlineChoice === PICKED_DEADLINE
	const canSubmit = !submitting && selectedPartyID !== ""

	const handleSubmit = async () => {
		if (!canSubmit) return

		if (deadlineIsPicked && !pickedDate) {
			notification.error("Please select a date")
			return
		}

		setSubmitting(true)

		try {
			await api.customer.parkCase(customerID, activeCase.CaseID, {
				WaitParty: selectedPartyID,
				WaitReason: waitReasonByPartyID[selectedPartyID],
				ChosenWaitUntil: deadlineIsPicked ? endOfDayInAgentTimezone(pickedDate) : undefined,
				AgentName: agentName,
				AgentEmail: agentEmail,
			})

			notification.success("Case parked")
			onParked()
		} catch (error: any) {
			notification.error(error.Message || "Failed to park case")
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogContent>
				<Box textAlign="center" mb={3} mt={1}>
					<Typography variant="h6" fontWeight={600} color={colors.endlessGalaxy["800"]} mb={0.5}>
						Park Case
					</Typography>
					<Chip label={activeCase.CaseType} size="small" variant="outlined" sx={{mt: 1}} />
				</Box>

				<Divider sx={{mb: 2}} />

				<Typography variant="subtitle1" fontWeight={600}>
					Who are we waiting on?
				</Typography>
				<Typography variant="body2" color="textSecondary" mb={1.5}>
					The case stays open while it waits.
				</Typography>
				<RadioGroup value={selectedPartyID} onChange={(e) => setSelectedPartyID(e.target.value)}>
					{activeCase.WaitParties.map((party) => (
						<WaitPartyRadio key={party.ID} party={party} selected={selectedPartyID === party.ID} />
					))}
				</RadioGroup>

				{waitRunsToDeadline && (
					<WaitDeadlineSection
						waitPeriodDays={activeCase.WaitPeriodDays}
						choice={deadlineChoice}
						onChoiceChange={setDeadlineChoice}
						pickedDate={pickedDate}
						onPickedDateChange={setPickedDate}
					/>
				)}
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 3, pt: 2}}>
				<Button
					onClick={onClose}
					variant="outlined"
					disabled={submitting}
					sx={{flex: 1, borderRadius: 2, color: colors.warmBlue["900"], borderColor: colors.warmBlue["900"]}}
				>
					CANCEL
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={!canSubmit}
					sx={{
						flex: 1,
						borderRadius: 2,
						backgroundColor: colors.warmBlue["900"],
						"&:hover": {backgroundColor: colors.warmBlue["800"]},
					}}
				>
					{submitting ? <CircularProgress size={20} color="inherit" /> : "PARK CASE"}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

function WaitPartyRadio({party, selected}: {party: CaseWaitParty; selected: boolean}) {
	const phrasing = phraseWaitParty(party)
	const escalation = party.Escalation
	// The verdict decides whether the option can be chosen; its reason decides what
	// the option says. A case that already carries a ticket is selectable and still
	// owes the agent its reason, or the option would promise a ticket it opens none of.
	const opensNoTicket = !!escalation && !escalation.OpensTicket
	const description =
		escalation?.Reason || (opensNoTicket ? "this case can open no client support ticket" : phrasing.Description)

	return (
		<FormControlLabel
			value={party.ID}
			control={<Radio size="small" />}
			disabled={opensNoTicket}
			sx={{
				ml: 0,
				mb: 1,
				border: `1px solid ${selected ? colors.warmBlue["400"] : colors.gray["200"]}`,
				borderRadius: 2,
				p: 1.5,
				backgroundColor: selected ? colors.warmBlue["50"] : "transparent",
			}}
			label={
				<Box>
					<Typography variant="body2" fontWeight={600}>
						{phrasing.Label}
					</Typography>
					{description && (
						<Typography variant="body2" color="textSecondary">
							{description}
						</Typography>
					)}
				</Box>
			}
		/>
	)
}

type WaitDeadlineSectionProps = {
	waitPeriodDays: number
	choice: string
	onChoiceChange: (choice: string) => void
	pickedDate: string
	onPickedDateChange: (pickedDate: string) => void
}

function WaitDeadlineSection({
	waitPeriodDays,
	choice,
	onChoiceChange,
	pickedDate,
	onPickedDateChange,
}: WaitDeadlineSectionProps) {
	return (
		<>
			<Typography variant="subtitle1" fontWeight={600} mt={2}>
				How long does it wait?
			</Typography>
			<RadioGroup value={choice} onChange={(e) => onChoiceChange(e.target.value)}>
				<FormControlLabel
					value={STANDARD_DEADLINE}
					control={<Radio size="small" />}
					label={<Typography variant="body2">Actioned within {waitPeriodDays} days</Typography>}
					sx={{ml: 0}}
				/>
				<FormControlLabel
					value={PICKED_DEADLINE}
					control={<Radio size="small" />}
					label={<Typography variant="body2">Pick a date</Typography>}
					sx={{ml: 0}}
				/>
			</RadioGroup>

			{choice === PICKED_DEADLINE && (
				<>
					<TextField
						type="date"
						label="Wait until"
						size="small"
						fullWidth
						value={pickedDate}
						onChange={(e) => onPickedDateChange(e.target.value)}
						slotProps={{inputLabel: {shrink: true}, htmlInput: {min: todayInAgentTimezone()}}}
						sx={{mt: 1}}
					/>
					<Alert severity="warning" sx={{mt: 2}}>
						Please note the reason for overriding the standard wait for this case type.
					</Alert>
				</>
			)}
		</>
	)
}

export {ParkCaseModal}
